import { Button, Card, CardContent } from '@mui/material'
import React from 'react'
import { WithTranslation, withTranslation } from 'react-i18next'
import { DataProvider, EffectStats, GroupInfos } from '../data-retrieval/DataProvider'
import { updateUrl } from '../url-helper'
import BarChart from './BarChart'
import { ChartData, convertChartData, groupIdComparator, limitData, nameToId, TableItem } from './data-helpers'
import DataTable from './DataTable'

interface Props extends WithTranslation {
	locale: string
	stats?: EffectStats | null
	selectedGroupIds: string[]
}

/**
 * A section to show one side effect.
 * Multiple can be listed on one page.
 */
class SideEffectIntro extends React.Component<Props, {
	groups?: GroupInfos
	selectedGroupIds: string[]
}> {
	private dataProvider = new DataProvider()
	private maxChartCount = 15

	private prevData?: ChartData

	constructor(props: Props) {
		super(props)

		const { selectedGroupIds } = this.props

		this.state = {
			groups: undefined,
			selectedGroupIds,
		}
	}

	async componentDidMount(): Promise<void> {
		const groups = await this.dataProvider.getGroups()

		this.setState({
			groups,
		})
	}

	render(): React.ReactNode {
		const { t, stats, locale } = this.props
		const { groups } = this.state
		if (!stats || !groups) {
			return (<div></div>)
		}
		const maxValue = this.getMaxValue()
		const { id: effectId, name: nameMapping, description: descriptionMapping, treatments } = stats
		const name = nameMapping[locale]
		const description = descriptionMapping[locale]
		const { data, selectedGroupIds } = this.getData()
		const chartData = limitData(data, this.maxChartCount)
		const prevData = this.prevData
		this.prevData = data

		const headers: TableItem[] = [
			{ value: "Treatment" },
			{ value: "Mentions (%)", align: 'right' },
		]
		const tableData = convertChartData(headers, data)

		return (<div>
			<h1 id={effectId} className={'title-h4'}>{name}</h1>
			<p>
				{/* TODO Convert markdown. */}
				{description}
			</p>

			{data.labels.length === 0 &&
				// Shouldn't happen because we should always find treatments for a mentioned side effect.
				<p>
					{"We have data for the people in this group but we didn't detect any side effects mentioned by them."}
				</p>
			}

			<Card className="pill0-card" variant="outlined">
				<CardContent>
					{data.labels.length > 0 && data.data.length > 0 &&
						<>
							<p>{"The following graph shows the percentage of people that mentioned "}{name.toLocaleLowerCase()}{" while using various drugs."}</p>
							<p>{"The percentages have been skewed slightly to preserve privacy."}</p>

							<div>
								<BarChart
									id={`bar-graph-${nameToId(name)}`}
									chartTitle={`Mentions of ${name} While Using Various Drugs`}
									data={chartData} maxHeight={500} maxValue={maxValue}
									prevData={prevData}
								/>
							</div>
							<p>
								{`${name} was most commonly reported by users of ${data.labels[0]} in the selected group.`}
							</p>
						</>
					}

					{/* Groups */}
					<div>
						<p>
							{"Click on one or more of the groups below to see how the group reports issues differently."}
						</p>

						{Object.keys(treatments).sort(groupIdComparator)
							.filter(groupId => !groups[groupId].isCombination)
							.map(groupId => {
								return <Button variant="contained"
									key={`effect-${groupId}`}
									color={selectedGroupIds.includes(groupId) ? 'primary' : 'secondary'}
									style={{ margin: '5px' }}
									// Disable groups that do not work as a combination.
									disabled={
										// Always allow the 'all' group.
										groupId !== 'all'
										// Allow clicking any group when data for all groups is shown.
										&& selectedGroupIds[0] !== 'all'
										// Allow clicking on the current group (to unselect it).
										&& !selectedGroupIds.includes(groupId)
										// Can this group be added to the current groups to make a valid group?
										&& !DataProvider.isValidGroup(groups, selectedGroupIds.concat(groupId))}
									onClick={() => this.selectGroup(groupId)}>
									{groups[groupId].name[locale]}
								</Button>
							})}
					</div>
				</CardContent>
			</Card>

			<Card className="pill0-card" variant="outlined">
				<CardContent>
					<p>
						{t('sideEffect.tableIntro')}
					</p>

					<DataTable data={tableData} />
				</CardContent>
			</Card>
		</div>
		)
	}

	private getMaxValue(): number | undefined {
		const { stats } = this.props
		if (!stats) {
			return undefined
		}
		let result = 0
		for (const treatments of Object.values(stats.treatments)) {
			for (const t of treatments) {
				if (t.prob > result) {
					result = t.prob
				}
			}
		}
		return result
	}

	private getData(): { data: ChartData, selectedGroupIds: string[] } {
		let { selectedGroupIds } = this.state
		let groupId = selectedGroupIds.join(',')
		const data = [], labels = [], hrefs = []
		const { stats, locale } = this.props
		const { groups } = this.state
		if (stats && groups) {
			let treatments = stats.treatments[groupId]
			if (!treatments) {
				// TODO Say there's no data for the combination.
				groupId = 'all'
				treatments = stats.treatments['all']
				selectedGroupIds = this.getSelectedGroupIds(groupId)
				updateUrl('groupIds', selectedGroupIds.join(','))
			}
			for (const t of treatments) {
				data.push(t.prob)
				if (t.name) {
					labels.push(t.name[locale])
				} else {
					// Shouldn't happen.
					labels.push("")
				}
				hrefs.push(`/treatment?id=${t.id}`)
			}
		}

		return {
			data: new ChartData(data, labels, hrefs),
			selectedGroupIds,
		}
	}

	getSelectedGroupIds(toggledGroupId: string): string[] {
		let selectedGroupIds: string[]
		if (toggledGroupId === 'all') {
			selectedGroupIds = [toggledGroupId]
		} else {
			selectedGroupIds = this.state.selectedGroupIds
			if (selectedGroupIds.length === 1 && selectedGroupIds[0] === 'all') {
				selectedGroupIds = []
			}
			const index = selectedGroupIds.indexOf(toggledGroupId)
			if (index > -1) {
				selectedGroupIds.splice(index, 1)
			} else {
				selectedGroupIds.push(toggledGroupId)
				selectedGroupIds.sort()
			}
		}
		return selectedGroupIds
	}

	selectGroup(toggledGroupId: string): void {
		const selectedGroupIds = this.getSelectedGroupIds(toggledGroupId)
		updateUrl('groupIds', selectedGroupIds.join(','))
		this.setState({ selectedGroupIds })
	}
}

export default withTranslation()(SideEffectIntro)